import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ArrowLeft, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../Sidebar";

export const AffiliateBuilderPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "La Stack Prime",
    slug: "",
    domain: "",
    tagline: "Le hub français des outils IA & productivité",
    lang: "fr",
    primary_color: "#0f172a",
    accent_color: "#ea580c",
    seed_ai_tools: true,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom de la marque est requis");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/affiliate/sites`, {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        domain: form.domain.trim() || undefined,
        tagline: form.tagline.trim() || undefined,
        lang: form.lang,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
      });
      const site = res.data;
      toast.success(`Site "${site.name}" créé`);

      if (form.seed_ai_tools) {
        try {
          const seedRes = await axios.post(
            `${API}/affiliate/sites/${site.id}/seed-all`,
            {}
          );
          const total =
            (seedRes.data.ia?.inserted?.length || 0) +
            (seedRes.data.creation_de_cours?.inserted?.length || 0) +
            (seedRes.data.emailing?.inserted?.length || 0);
          toast.success(`${total} outils seed (IA + Cours + Emailing)`);
        } catch {
          toast.error("Échec du seed all (site créé quand même)");
        }
      }
      navigate(`/admin/affiliate/sites/${site.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Échec de la création";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="affiliate-builder-page">
      <Link
        to="/admin/affiliate/sites"
        className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Retour aux sites
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-orange-500" />
        <h2
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Nouveau site Affiliate
        </h2>
      </div>
      <p className="text-slate-600 mb-6">
        Configurez votre marque ombrelle (style « La Stack Prime ») — vous
        ajouterez ensuite catégories, outils, stacks et comparatifs.
      </p>

      <Card>
        <CardContent className="p-6 space-y-5">
          <div>
            <Label htmlFor="aff-name">Nom de la marque *</Label>
            <Input
              id="aff-name"
              data-testid="aff-name-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Ex: La Stack Prime"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="aff-slug">Slug (URL-safe)</Label>
              <Input
                id="aff-slug"
                data-testid="aff-slug-input"
                value={form.slug}
                onChange={(e) => update("slug", e.target.value)}
                placeholder="auto-généré"
              />
            </div>
            <div>
              <Label htmlFor="aff-lang">Langue</Label>
              <select
                id="aff-lang"
                data-testid="aff-lang-select"
                value={form.lang}
                onChange={(e) => update("lang", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="aff-domain">Domaine (optionnel)</Label>
            <Input
              id="aff-domain"
              data-testid="aff-domain-input"
              value={form.domain}
              onChange={(e) => update("domain", e.target.value)}
              placeholder="https://lastackprime.fr"
            />
          </div>

          <div>
            <Label htmlFor="aff-tagline">Tagline / Slogan</Label>
            <Textarea
              id="aff-tagline"
              data-testid="aff-tagline-input"
              value={form.tagline}
              onChange={(e) => update("tagline", e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="aff-primary">Couleur primaire</Label>
              <div className="flex gap-2 items-center">
                <input
                  id="aff-primary"
                  data-testid="aff-primary-color"
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => update("primary_color", e.target.value)}
                  className="h-10 w-12 rounded border border-slate-200"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => update("primary_color", e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="aff-accent">Couleur d'accent (CTA)</Label>
              <div className="flex gap-2 items-center">
                <input
                  id="aff-accent"
                  data-testid="aff-accent-color"
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => update("accent_color", e.target.value)}
                  className="h-10 w-12 rounded border border-slate-200"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => update("accent_color", e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-md border border-orange-200 bg-orange-50/50 cursor-pointer">
            <input
              type="checkbox"
              data-testid="aff-seed-checkbox"
              checked={form.seed_ai_tools}
              onChange={(e) => update("seed_ai_tools", e.target.checked)}
              className="mt-1 accent-orange-500"
            />
            <div>
              <div className="font-medium text-slate-900 flex items-center gap-1">
                <Wand2 className="w-4 h-4 text-orange-500" />
                Seed automatique : 43 outils curés (5 catégories) — 18 🇫🇷
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Crée 5 catégories (IA · Cours · Emailing · Caisse · Compta FR)
                et injecte 43 outils avec verdicts PME/Freelance/Agence, dont
                18 alternatives françaises mises en avant (Mistral AI, Dust,
                360Learning, Pennylane, Indy, PayPlug, Sellsy, Brevo, Mailjet…).
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Link to="/admin/affiliate/sites">
              <Button variant="outline">Annuler</Button>
            </Link>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="aff-create-submit"
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? "Création…" : "Créer le site"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
